import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlay,
  faWandMagicSparkles,
  faEye,
  faCircleNotch, // Para 'CREATING_DRAFT', 'REQUESTING_URL', 'CONFIRMING'
  faUpload, // Para 'UPLOADING'
} from '@fortawesome/free-solid-svg-icons';
import FileUpload from '../../../../components/common/FileUpload';
import MediaPreview from '../../../../components/common/MediaPreview';

// --- NUEVO COMPONENTE DE PROGRESO ---
const UploadProgressIndicator = ({
  uploadStatus,
  uploadProgress,
  UPLOAD_STATE,
}) => {
  const { t } = useTranslation();
  let message = '';
  let showProgress = false;
  let icon = <FontAwesomeIcon icon={faCircleNotch} spin />;

  switch (uploadStatus) {
    case UPLOAD_STATE.CREATING_DRAFT:
      message = t('post_upload.status_creating', 'Creando post...');
      break;
    case UPLOAD_STATE.REQUESTING_URL:
      message = t('post_upload.status_requesting', 'Solicitando URL...');
      break;
    case UPLOAD_STATE.UPLOADING:
      message = t('post_upload.status_uploading', 'Subiendo... {{progress}}%', {
        progress: uploadProgress,
      });
      showProgress = true;
      icon = <FontAwesomeIcon icon={faUpload} />;
      break;
    case UPLOAD_STATE.CONFIRMING:
      message = t('post_upload.status_confirming', 'Confirmando subida...');
      break;
    default:
      return null;
  }

  return (
    <div className="absolute inset-0 bg-black/70 z-30 flex flex-col items-center justify-center text-white rounded-xl">
      <div className="flex items-center gap-2 text-lg font-semibold">
        {icon}
        <span>{message}</span>
      </div>
      {showProgress && (
        <div className="w-10/12 bg-neutral-600 rounded-full h-2.5 mt-3">
          <div
            className="bg-primary h-2.5 rounded-full transition-all duration-300"
            style={{ width: `${uploadProgress}%` }}
          ></div>
        </div>
      )}
    </div>
  );
};
// ------------------------------------

const PostFileUpload = ({
  file,
  thumbnailFile,
  onFileSelect,
  isPremium,
  onEditEffect,
  fileEffect,
  label,
  description,
  isUserView,
  // --- NUEVAS PROPS ---
  uploadStatus,
  uploadProgress,
  UPLOAD_STATE,
  isPending, // Para deshabilitar la eliminación mientras se sube
  // --------------------
}) => {
  const { t } = useTranslation();
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    let objectUrl = null;
    const fileToPreview = thumbnailFile || file;

    if (fileToPreview) {
      objectUrl = URL.createObjectURL(fileToPreview);
      setPreviewUrl(objectUrl);
    } else {
      setPreviewUrl(null);
    }

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [file, thumbnailFile]);

  if (!file) {
    return (
      <div>
        <label className="block text-sm font-medium text-neutral-800 dark:text-neutral-200">
          {label} <span className="text-red-500">*</span>
        </label>
        {description && (
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-2">
            {description}
          </p>
        )}
        <FileUpload
          onFileSelect={(selectedFile) => onFileSelect(selectedFile)}
          accept="image/*,video/mp4,video/quicktime,video/x-matroska,video/webm"
          multiple={false}
          disabled={isPending} // Deshabilitar si se está subiendo
        />
      </div>
    );
  }

  const isVideo = file.type.startsWith('video/');

  return (
    <div>
      <label className="block text-sm font-medium text-neutral-800 dark:text-neutral-200">
        {label}
      </label>
      <div className="mt-2 relative">
        {' '}
        {/* --- Añadido relative --- */}
        <MediaPreview
          preview={{ url: previewUrl, type: isVideo ? 'video' : 'image' }}
          onRemove={() => onFileSelect(null)}
          isPremium={isPremium}
          onEditEffect={onEditEffect}
          isUserView={isUserView}
          effect={fileEffect}
          disabled={isPending} // Deshabilitar botones durante la subida
        >
          {isVideo && !isUserView && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/30 pointer-events-none">
              <span className="bg-black/50 text-white rounded-full h-10 w-10 flex items-center justify-center">
                <FontAwesomeIcon icon={faPlay} className="text-lg ml-0.5" />
              </span>
            </div>
          )}

          {fileEffect && !isUserView && (
            <div className="absolute bottom-2 left-2 bg-primary text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg flex items-center gap-1.5 z-10 pointer-events-none">
              <FontAwesomeIcon icon={faWandMagicSparkles} />
              <span className="capitalize">
                {fileEffect.type.replace('_', ' ')}: {fileEffect.intensity}
              </span>
            </div>
          )}

          {isUserView && fileEffect && (
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex flex-col items-center justify-center text-white p-2 text-center z-20">
              <FontAwesomeIcon icon={faEye} className="text-xl mb-1" />
              <span className="font-semibold text-xs">
                {t('preview.user_view_active', 'Vista de Usuario')}
              </span>
            </div>
          )}
        </MediaPreview>
        {/* --- INDICADOR DE PROGRESO AÑADIDO --- */}
        {isPending && (
          <UploadProgressIndicator
            uploadStatus={uploadStatus}
            uploadProgress={uploadProgress}
            UPLOAD_STATE={UPLOAD_STATE}
          />
        )}
        {/* ------------------------------------- */}
      </div>
    </div>
  );
};

export default PostFileUpload;
